import React, { useCallback } from "react";
import { css } from "@emotion/react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { Font, Icon, TextLink } from "@moneyforward/mfx_biz_react_components";

import { PageTitle } from "@/components/molecules";
import { ForgotPasswordSendMailForm } from "@/components/organisms";
import { MainTemplate } from "@/components/templates";

import {
  SendForgotPasswordMailMutation,
  useSendForgotPasswordMailMutation,
} from "@/generated/graphql";
import useRollbarUtil from "../hooks/useRollbarUtil";

type FormValues = {
  email: string;
};

export const ForgotPasswordSendMailPage = () => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const router = useRouter();

  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      email: "",
    },
  });

  const [sendForgotPasswordMailMutation] = useSendForgotPasswordMailMutation({
    onCompleted: (data: SendForgotPasswordMailMutation) => {
      if (data.sendForgotPasswordMail) {
        router.push("/forgot-password-send-mail-completed");
      }
    },
    onError: getNotifyErrorHandlerForGraphQL(
      "Faild to send forgot password mail",
      "メールの送付に失敗しました"
    ),
  });

  const handleSubmit = useCallback(
    async ({ email }: FormValues) => {
      const { app } = router.query;
      await sendForgotPasswordMailMutation({
        variables: {
          input: {
            email,
            app: app ? String(app) : "",
          },
        },
      });
    },
    [sendForgotPasswordMailMutation]
  );

  return (
    <MainTemplate title={`パスワード変更メールの送信`}>
      <PageTitle
        mainTitle={`パスワード変更メールの送信`}
        subTitle={
          <>
            <div>パスワードを忘れた方は、登録したメールアドレスを入力し</div>
            <div>送付されたメールからパスワード変更をおこなってください。</div>
          </>
        }
      />
      <FormProvider {...methods}>
        <ForgotPasswordSendMailForm onSubmit={handleSubmit} />
      </FormProvider>
      <div css={bottomMessage}>
        <Font variant="l" component="p">
          管理者以外の方でパスワードを忘れた場合は、
          <br />
          企業管理者へお問い合わせください。
        </Font>
      </div>
      <div css={bottomMessage}>
        <Font variant="l" component="p">
          ログインにお困りの方は、
          <TextLink
            target="_blank"
            href="https://support.id.x.moneyforward.com/hc/ja/sections/16489247377305"
          >
            <span
              css={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              よくあるご質問
              <Icon name="openInNew" size="sm" css={{ cursor: "pointer" }} />
            </span>
          </TextLink>{" "}
          をご覧ください。
        </Font>
      </div>
    </MainTemplate>
  );
};

const bottomMessage = css({
  display: "flex",
  justifyContent: "center",
  marginTop: "32px",
});
